import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  CustomVariable,
  EmailBranding,
  EmailTemplate,
  EmailTemplateFilters,
  EmailTemplateVersion,
  ProseMirrorDoc,
  EmailVariableRegistryEntry,
  EMAIL_TEMPLATE_CATEGORY,
  EMAIL_TEMPLATE_STATUS,
} from "@/types/(company)/email-template.interface";

// ─── Request / response shapes ────────────────────────────────────────────────

export interface ListEmailTemplatesQuery {
  page?: number;
  limit?: number;
  status?: EMAIL_TEMPLATE_STATUS;
  category?: EMAIL_TEMPLATE_CATEGORY;
  search?: string;
}

export interface CreateEmailTemplateBody {
  name: string;
  description?: string;
  category: EMAIL_TEMPLATE_CATEGORY;
}

export interface UpdateEmailTemplateMetadataBody {
  name?: string;
  description?: string;
  category?: EMAIL_TEMPLATE_CATEGORY;
  branding?: EmailBranding;
  customVariables?: CustomVariable[];
}

export interface UpdateEmailTemplateContentBody {
  contentJson: ProseMirrorDoc;
  subject?: string;
  preheader?: string;
  branding?: EmailBranding;
  customVariables?: CustomVariable[];
}

export interface AiGenerateEmailContentBody {
  prompt: string;
  language?: string;
  currentContent?: Record<string, unknown>;
}

export interface AiGenerateEmailContentResponse {
  contentJson: Record<string, unknown>;
  message: string | null;
}

// ─── API slice ────────────────────────────────────────────────────────────────
// Mirrors documentTemplatesApi (minus generate / generated-documents / AI).
// Responses keep the backend `IApiResponse<T>` envelope; call sites read
// `res.data` themselves (no `transformResponse`).

export const emailTemplatesApi = createApi({
  reducerPath: "emailTemplatesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["EmailTemplates", "EmailTemplate", "EmailVariableRegistry"],
  endpoints: (builder) => ({
    // ── List (paginated) ──────────────────────────────────────────────────
    listEmailTemplates: builder.query<
      IApiResponse<EmailTemplate[]>,
      EmailTemplateFilters | ListEmailTemplatesQuery
    >({
      query: (q) => ({
        url: "/company/email-templates",
        method: "GET",
        params: {
          page: q.page,
          limit: q.limit,
          search: "search" in q && q.search ? q.search : undefined,
          status: q.status !== "all" ? q.status : undefined,
          category: q.category !== "all" ? q.category : undefined,
        },
      }),
      providesTags: ["EmailTemplates"],
    }),

    // ── Detail ────────────────────────────────────────────────────────────
    getEmailTemplate: builder.query<IApiResponse<EmailTemplate>, number>({
      query: (id) => ({
        url: `/company/email-templates/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "EmailTemplate", id }],
    }),

    // ── Create draft ──────────────────────────────────────────────────────
    createEmailTemplate: builder.mutation<
      IApiResponse<EmailTemplate>,
      CreateEmailTemplateBody
    >({
      query: (body) => ({
        url: "/company/email-templates",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EmailTemplates"],
    }),

    // ── Metadata patch ────────────────────────────────────────────────────
    updateEmailTemplateMetadata: builder.mutation<
      IApiResponse<EmailTemplate>,
      { id: number; body: UpdateEmailTemplateMetadataBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/email-templates/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "EmailTemplates",
        { type: "EmailTemplate", id },
      ],
    }),

    // ── Autosave content (subject + preheader + body) ─────────────────────
    updateEmailTemplateContent: builder.mutation<
      IApiResponse<EmailTemplateVersion>,
      { id: number; body: UpdateEmailTemplateContentBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/email-templates/${id}/content`,
        method: "PATCH",
        body,
      }),
      // Invalidate the list too — a content save changes the subject preview,
      // updatedAt and (for a published template) the status shown in listings.
      invalidatesTags: (_r, _e, { id }) => [
        "EmailTemplates",
        { type: "EmailTemplate", id },
      ],
    }),

    // ── Publish ───────────────────────────────────────────────────────────
    publishEmailTemplate: builder.mutation<
      IApiResponse<EmailTemplate>,
      number
    >({
      query: (id) => ({
        url: `/company/email-templates/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "EmailTemplates",
        { type: "EmailTemplate", id },
      ],
    }),

    // ── Unpublish ─────────────────────────────────────────────────────────
    unpublishEmailTemplate: builder.mutation<
      IApiResponse<EmailTemplate>,
      number
    >({
      query: (id) => ({
        url: `/company/email-templates/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "EmailTemplates",
        { type: "EmailTemplate", id },
      ],
    }),

    // ── Duplicate ─────────────────────────────────────────────────────────
    duplicateEmailTemplate: builder.mutation<
      IApiResponse<EmailTemplate>,
      number
    >({
      query: (id) => ({
        url: `/company/email-templates/${id}/duplicate`,
        method: "POST",
      }),
      invalidatesTags: ["EmailTemplates"],
    }),

    // ── Archive ───────────────────────────────────────────────────────────
    archiveEmailTemplate: builder.mutation<
      IApiResponse<EmailTemplate>,
      number
    >({
      query: (id) => ({
        url: `/company/email-templates/${id}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "EmailTemplates",
        { type: "EmailTemplate", id },
      ],
    }),

    // ── Soft delete ───────────────────────────────────────────────────────
    deleteEmailTemplate: builder.mutation<IApiResponse<null>, number>({
      query: (id) => ({
        url: `/company/email-templates/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EmailTemplates"],
    }),

    // ── Preview (server-side render to inbox-safe HTML) ───────────────────
    previewEmailTemplate: builder.mutation<
      IApiResponse<{ html: string }>,
      { id: number; employeeId?: number }
    >({
      query: ({ id, employeeId }) => ({
        url: `/company/email-templates/${id}/preview`,
        method: "POST",
        body: { employeeId },
      }),
    }),

    // ── Variable registry ─────────────────────────────────────────────────
    getEmailVariableRegistry: builder.query<
      IApiResponse<EmailVariableRegistryEntry[]>,
      void
    >({
      query: () => ({
        url: "/company/email-templates/variables",
        method: "GET",
      }),
      providesTags: ["EmailVariableRegistry"],
    }),

    // ── AI body generation ─────────────────────────────────────────────────
    aiGenerateEmailContent: builder.mutation<
      IApiResponse<AiGenerateEmailContentResponse>,
      { id: number; body: AiGenerateEmailContentBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/email-templates/${id}/ai-generate`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useListEmailTemplatesQuery,
  useGetEmailTemplateQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMetadataMutation,
  useUpdateEmailTemplateContentMutation,
  usePublishEmailTemplateMutation,
  useUnpublishEmailTemplateMutation,
  useDuplicateEmailTemplateMutation,
  useArchiveEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  usePreviewEmailTemplateMutation,
  useGetEmailVariableRegistryQuery,
  useAiGenerateEmailContentMutation,
} = emailTemplatesApi;
